import { motion } from 'framer-motion';

const integrations = [
  { name: 'Google Ads', icon: '🎯', color: '#4285F4' },
  { name: 'Facebook Ads', icon: '📱', color: '#1877F2' },
  { name: 'TikTok Ads', icon: '🎵', color: '#000000' },
  { name: 'ChatGPT', icon: '🤖', color: '#10A37F' },
  { name: 'GoHighLevel', icon: '📈', color: '#FF6B6B' },
  { name: 'Calendly', icon: '📅', color: '#006BFF' },
  { name: 'Slack', icon: '💬', color: '#4A154B' },
  { name: 'Stripe', icon: '💳', color: '#008CDD' }
];

const automationSteps = [
  {
    title: 'Génération de Leads',
    description: 'Campagnes publicitaires multi-canaux optimisées par l\'IA',
    icon: '🎯',
    platforms: ['Google Ads', 'Facebook', 'TikTok'],
    metrics: { value: '30%', label: 'Réduction CPA' }
  },
  {
    title: 'Qualification IA',
    description: 'Chatbot IA qui qualifie et score vos prospects',
    icon: '🤖',
    platforms: ['ChatGPT', 'GoHighLevel'],
    metrics: { value: '85%', label: 'Taux de qualification' }
  },
  {
    title: 'Prise de RDV',
    description: 'Booking automatique sur votre agenda',
    icon: '📅',
    platforms: ['Calendly', 'Google Calendar'],
    metrics: { value: '24/7', label: 'Disponibilité' }
  },
  {
    title: 'Notifications',
    description: 'Alertes en temps réel pour votre équipe',
    icon: '📱',
    platforms: ['Slack', 'WhatsApp', 'Email'],
    metrics: { value: '< 1min', label: 'Temps de réponse' }
  }
];

const AIAutomationSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Column - Sales Copy */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.h2
              className="text-4xl font-bold leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Lancez votre tunnel de vente IA,
              <span className="bg-gradient-to-r from-neon-orange to-[#FF4500] bg-clip-text text-transparent"> connecté et automatisé de A à Z</span>
            </motion.h2>

            <motion.p
              className="text-xl text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Grâce à CSD et l'IA, on connecte tous vos outils pour générer, 
              qualifier et closer vos prospects en continu.
            </motion.p>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {[
                'Campagnes automatisées sur Google Ads, Facebook & TikTok',
                'Qualification des leads par IA (chatbot / email / WhatsApp)',
                'RDV bookés automatiquement sur votre agenda',
                'Notification en temps réel via Slack, SMS ou Telegram',
                'Intégration complète GoHighLevel, Zapier, Stripe, Notion'
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-neon-orange">✓</span>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </motion.div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-neon-orange to-[#FF4500] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
            >
              <span className="relative z-10">Voir la démo en direct →</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 1 }}
              />
            </motion.button>
          </motion.div>

          {/* Right Column - Visual Make.com Simulation */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Workflow Container */}
            <div className="bg-white rounded-xl shadow-xl p-8 relative">
              <div className="space-y-6">
                {automationSteps.map((step, index) => (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Connector Line */}
                    {index < automationSteps.length - 1 && (
                      <div className="absolute left-6 top-12 w-1 h-16 bg-gradient-to-b from-neon-orange/20 to-transparent" />
                    )}

                    {/* Step Content */}
                    <div className="relative bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-start gap-4">
                        <div className="text-3xl">{step.icon}</div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                          <p className="text-gray-600 text-sm mb-4">{step.description}</p>
                          
                          {/* Platforms */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {step.platforms.map((platform) => (
                              <span
                                key={platform}
                                className="px-3 py-1 text-sm bg-white rounded-full text-gray-600 shadow-sm"
                              >
                                {platform}
                              </span>
                            ))}
                          </div>

                          {/* Metrics */}
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-neon-orange">
                              {step.metrics.value}
                            </span>
                            <span className="text-gray-500">
                              {step.metrics.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Integration Icons */}
              <div className="absolute -right-4 top-1/2 -translate-y-1/2 space-y-4">
                {integrations.map((integration, index) => (
                  <motion.div
                    key={integration.name}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="w-8 h-8 rounded-lg bg-white shadow-lg flex items-center justify-center"
                    style={{ color: integration.color }}
                  >
                    {integration.icon}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Animated Connection Lines */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <svg className="w-full h-full">
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FF6A00" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#FF6A00" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[...Array(8)].map((_, i) => (
                  <motion.path
                    key={i}
                    d={`M ${Math.random() * 100},${Math.random() * 100} C ${Math.random() * 200},${Math.random() * 200} ${Math.random() * 300},${Math.random() * 300} ${Math.random() * 400},${Math.random() * 400}`}
                    stroke="url(#lineGradient)"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut",
                      delay: i * 0.2
                    }}
                  />
                ))}
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AIAutomationSection;